import React, { useEffect, useRef, useMemo, useCallback } from 'react';

// Lerp factor per cube — lower = smoother/slower follow (0.02–0.08 gives that joco feel)
const LERP_BASE = 0.03;

const FloatingCubes = () => {
  const containerRef = useRef(null);
  const cubeRefs = useRef([]);
  const innerRefs = useRef([]);
  const rafRef = useRef(null);

  // Mutable state for the animation loop (no React re-renders)
  const state = useRef({
    scrollTarget: 0,    // actual scroll position (target)
    cubes: [],          // per-cube mutable animation state
  });

  // Generate cube configs once
  const cubeConfigs = useMemo(() => Array.from({ length: 18 }, (_, i) => ({
    id: i,
    left: (i * 17.3 + 7) % 100,
    top: (i * 23.7 + 5) % 300,
    size: 30 + (i * 13) % 70,
    color: i % 3 === 0 ? 'red' : i % 3 === 1 ? 'amber' : 'red-dark',
    // Parallax speed
    speed: 0.15 + (i * 0.07) % 0.65,
    // Continuous rotation speed (radians per frame, like joco)
    contRotX: (0.1 + (i * 0.07) % 0.4) * (i % 2 === 0 ? 1 : -1),
    contRotY: (0.15 + (i * 0.09) % 0.5) * (i % 3 === 0 ? -1 : 1),
    contRotZ: (0.03 + (i * 0.04) % 0.15) * (i % 2 === 0 ? 1 : -1),
    // Horizontal drift factor
    driftX: ((i % 2 === 0) ? 1 : -1) * (0.02 + (i * 0.01) % 0.06),
    // Lerp factor — each cube follows at different smoothness
    lerp: LERP_BASE + (i * 0.005) % 0.05,
    // Scale
    scaleBase: 0.8 + (i * 0.03) % 0.4,
    // Opacity
    opacity: 0.08 + (i * 0.012) % 0.12,
  })), []);

  // Initialize mutable per-cube state
  useEffect(() => {
    state.current.cubes = cubeConfigs.map(c => ({
      // Current (lerped) values — these smoothly follow targets
      currentY: 0,
      currentX: 0,
      currentScale: c.scaleBase,
      // Accumulated rotation (continuous, not tied to scroll)
      rotX: (c.id * 47) % 360,
      rotY: (c.id * 63) % 360,
      rotZ: (c.id * 29) % 360,
    }));
  }, [cubeConfigs]);

  // The animation loop — runs every frame regardless of scroll
  const animate = useCallback(() => {
    const { scrollTarget, cubes } = state.current;

    cubeConfigs.forEach((config, i) => {
      const cube = cubes[i];
      if (!cube) return;

      const el = cubeRefs.current[i];
      const inner = innerRefs.current[i];
      if (!el || !inner) return;

      // Target positions based on actual scroll
      const targetY = -scrollTarget * config.speed;
      const targetX = scrollTarget * config.driftX;
      const targetScale = config.scaleBase + Math.sin(scrollTarget * 0.002 + i) * 0.1;

      // Lerp toward targets (the key smooth effect)
      cube.currentY += (targetY - cube.currentY) * config.lerp;
      cube.currentX += (targetX - cube.currentX) * config.lerp;
      cube.currentScale += (targetScale - cube.currentScale) * config.lerp;

      // Continuous rotation — adds small amount each frame regardless of scroll
      cube.rotX += config.contRotX;
      cube.rotY += config.contRotY;
      cube.rotZ += config.contRotZ;

      // Apply transforms directly to DOM (no React re-render)
      el.style.transform = `translate3d(${cube.currentX}px, ${cube.currentY}px, 0) scale(${cube.currentScale})`;
      inner.style.transform = `rotateX(${cube.rotX}deg) rotateY(${cube.rotY}deg) rotateZ(${cube.rotZ}deg)`;
    });

    rafRef.current = requestAnimationFrame(animate);
  }, [cubeConfigs]);

  // Start animation loop and scroll listener
  useEffect(() => {
    const handleScroll = () => {
      state.current.scrollTarget = window.scrollY;
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    handleScroll();

    // Start the continuous animation loop
    rafRef.current = requestAnimationFrame(animate);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [animate]);

  return (
    <div
      ref={containerRef}
      className="absolute inset-0 pointer-events-none overflow-hidden"
      style={{ zIndex: 1, perspective: '1200px' }}
    >
      {cubeConfigs.map((cube, i) => (
        <div
          key={cube.id}
          ref={el => cubeRefs.current[i] = el}
          className="absolute"
          style={{
            left: `${cube.left}%`,
            top: `${cube.top}vh`,
            width: cube.size,
            height: cube.size,
            opacity: cube.opacity,
            willChange: 'transform',
          }}
        >
          <div
            ref={el => innerRefs.current[i] = el}
            className="cube-3d-scroll"
            style={{
              width: '100%',
              height: '100%',
              transformStyle: 'preserve-3d',
            }}
          >
            {/* Front */}
            <div
              className={`cube-face ${
                cube.color === 'red' ? 'bg-aspire-red' :
                cube.color === 'amber' ? 'bg-amber-500' :
                'bg-red-800'
              }`}
              style={{ transform: `rotateY(0deg) translateZ(${cube.size / 2}px)` }}
            />
            {/* Back */}
            <div
              className={`cube-face ${
                cube.color === 'red' ? 'bg-red-900' :
                cube.color === 'amber' ? 'bg-amber-700' :
                'bg-red-950'
              }`}
              style={{ transform: `rotateY(180deg) translateZ(${cube.size / 2}px)` }}
            />
            {/* Right */}
            <div
              className={`cube-face ${
                cube.color === 'red' ? 'bg-red-800' :
                cube.color === 'amber' ? 'bg-amber-600' :
                'bg-red-900'
              }`}
              style={{ transform: `rotateY(90deg) translateZ(${cube.size / 2}px)` }}
            />
            {/* Left */}
            <div
              className={`cube-face ${
                cube.color === 'red' ? 'bg-red-800' :
                cube.color === 'amber' ? 'bg-amber-600' :
                'bg-red-900'
              }`}
              style={{ transform: `rotateY(-90deg) translateZ(${cube.size / 2}px)` }}
            />
            {/* Top */}
            <div
              className={`cube-face ${
                cube.color === 'red' ? 'bg-red-700' :
                cube.color === 'amber' ? 'bg-amber-400' :
                'bg-red-800'
              }`}
              style={{ transform: `rotateX(90deg) translateZ(${cube.size / 2}px)` }}
            />
            {/* Bottom */}
            <div
              className={`cube-face ${
                cube.color === 'red' ? 'bg-red-900' :
                cube.color === 'amber' ? 'bg-amber-700' :
                'bg-red-950'
              }`}
              style={{ transform: `rotateX(-90deg) translateZ(${cube.size / 2}px)` }}
            />
          </div>
        </div>
      ))}
    </div>
  );
};

export default FloatingCubes;
